import { FC, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import theme from "@/theme";
import HeartSvg from "@/assets/svg/HeartSvg";
import { styles } from "../stylesLeaderboard";
import { useTopLeaderboardQuery } from "@/store/api/leaderboardApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { useNavigate } from "react-router-dom";
import { UserType } from "@/store/api/authApiData";
import Polygon from "@/assets/svg/Polygon";

export type LeaderboardUser = UserType & {
  id: string;
  totalLikes: number;
  likesPerPost: number;
};

interface ITableProps {
  tabValue: number;
  sortVariant: string;
  order: string;
  setOrder: (order: string) => void;
  filterTimeTrendingUsers: string;
  setSortVariant: (variant: string) => void;
}

const TableLeaderboard: FC<ITableProps> = ({
  tabValue,
  sortVariant,
  order,
  setOrder,
  filterTimeTrendingUsers,
  setSortVariant,
}) => {
  const [users, setUsers] = useState<LeaderboardUser[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy, setSortBy] = useState<"likes" | "likesPerPost">(
    sortVariant === "Likes" ? "likes" : "likesPerPost"
  );

  const limit = 10;
  const Params = {
    page,
    limit,
    sortBy: sortBy,
    timeframe:
      filterTimeTrendingUsers === ""
        ? "1_day"
        : filterTimeTrendingUsers === "All time"
          ? "all_time"
          : filterTimeTrendingUsers.replace(" ", "_"),
  };
  const [loadingPage, setLoadingPage] = useState(false);

  const queryParams = useMemo(
    () => ({
      sortBy,
      limit,
      timeframe: Params.timeframe,
      page,
      order,
    }),
    [sortBy, limit, Params.timeframe, page, order]
  );

  const navigate = useNavigate();
  const { data, isLoading } = useTopLeaderboardQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setSortBy(sortVariant === "Likes" ? "likes" : "likesPerPost");
    setPage(1);
    setHasMore(tabValue === 0);
    setUsers([]);
  }, [sortVariant, filterTimeTrendingUsers, tabValue]);

  useEffect(() => {
    if (!data?.users) return;

    const mappedUsers: LeaderboardUser[] = data.users.map((u: any) => ({
      ...u,
      id: u.uid,
      totalLikes: u.totalLikes,
      likesPerPost: u.likesPerPost,
    }));

    if (tabValue === 1) {
      setUsers(mappedUsers);
      setHasMore(false);
    } else {
      setUsers((prev) => {
        if (page === 1) return mappedUsers;

        const newUsers = mappedUsers.filter(
          (newUser) => !prev.some((user) => user.id === newUser.id)
        );
        return [...prev, ...newUsers];
      });

      setHasMore(mappedUsers.length === limit);
    }
  }, [data, tabValue, limit, page]);

  const handleSortClick = () => {
    setOrder(order === "asc" ? "desc" : "asc");
  };

  const hideDesc = order === "asc" ? false : true;
  const hideAsc = order === "asc" ? true : false;
  const sortedUsers = [...users].map((user, index) => ({ ...user, position: index + 1 }));
  if (order === "asc") sortedUsers.reverse();

  const handleClick = (uid: string) => {
    navigate(`/profile/${uid}`);
  };
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loadingPage) {
      setLoadingPage(true);
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (data?.users) setLoadingPage(false);
  }, [data]);

  useEffect(() => {
    setSortBy(sortVariant === "Likes" ? "likes" : "likesPerPost");
    setPage(1);
    setHasMore(true);
  }, [sortVariant, filterTimeTrendingUsers]);

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Box sx={{ borderRadius: "0.5rem" }}>
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead sx={{ backgroundColor: "#F5F9FE" }}>
            <TableRow sx={{ display: "flex" }}>
              {tabValue === 1 && (
                <TableCell sx={[styles.tableCellHead, { width: "3.75rem", cursor: "pointer" }]}>
                  #
                </TableCell>
              )}
              <TableCell
                sx={[styles.tableCellHead, { width: "100%", maxWidth: "644px", cursor: "default" }]}
              >
                User
              </TableCell>
              <TableCell
                align="right"
                sx={[styles.tableCellHead, { width: "160px" }]}
                onClick={() => {
                  handleSortClick();
                  setSortVariant("Likes");
                }}
              >
                <Box display="flex" alignItems="center">
                  Likes
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "20px",
                      height: "20px",
                      padding: "1px",
                      opacity: 1,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        marginBottom: "1px",
                        display: "flex",
                        flexDirection: "column",
                        width: "12px",
                        height: "18px",
                        gap: "2px",
                        opacity: 1,
                        cursor: "pointer",
                      }}
                    >
                      <Box sx={{ display: "contents" }}>
                        <Polygon
                          color={
                            hideAsc && sortVariant === "Likes"
                              ? theme.palette.custom.mainBlue
                              : "#2A2D31"
                          }
                        />
                      </Box>

                      <Box sx={{ display: "contents" }}>
                        <Polygon
                          flipped
                          color={
                            hideDesc && sortVariant === "Likes"
                              ? theme.palette.custom.mainBlue
                              : "#2A2D31"
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </TableCell>
              <TableCell
                sx={[styles.tableCellHead, { width: "160px" }]}
                onClick={() => {
                  handleSortClick();
                  setSortVariant("Likes/Post");
                }}
              >
                <Box display="flex" alignItems="center">
                  Likes / post
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "20px",
                      height: "20px",
                      padding: "1px",
                      opacity: 1,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        marginBottom: "1px",
                        display: "flex",
                        flexDirection: "column",
                        width: "12px",
                        height: "18px",
                        gap: "2px",
                        opacity: 1,
                        cursor: "pointer",
                      }}
                    >
                      <Box sx={{ display: "contents" }}>
                        <Polygon
                          color={
                            hideAsc && sortVariant === "Likes/Post"
                              ? theme.palette.custom.mainBlue
                              : "#2A2D31"
                          }
                        />
                      </Box>

                      <Box sx={{ display: "contents" }}>
                        <Polygon
                          flipped
                          color={
                            hideDesc && sortVariant === "Likes/Post"
                              ? theme.palette.custom.mainBlue
                              : "#2A2D31"
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <Box
            sx={{
              height: "calc(100vh - 280px)",
              overflowY: "auto",
              display: "block",
            }}
            onScroll={tabValue === 0 ? handleScroll : undefined}
          >
            {isLoading && users.length === 0 ? (
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 4 }}>
                <CustomLoader size={48} loading />
              </Box>
            ) : (
              <TableBody sx={{ display: "block" }}>
                {users.map((user, index) => (
                  <TableRow key={user.id} sx={{ display: "flex", justifyContent: "space-between" }}>
                    {tabValue === 1 && (
                      <TableCell sx={[styles.tableCellBody, { width: "3.75rem" }]}>
                        <Typography
                          variant="h5"
                          fontWeight={500}
                          lineHeight="145%"
                          color={theme.palette.custom.blueGray900}
                        >
                          {index + 1}.
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell sx={[styles.tableCellBody, { width: "100%", maxWidth: "644px" }]}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap="0.75rem"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleClick(user.uid)}
                      >
                        <Avatar
                          src={user?.image?.url}
                          alt={user.name}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight={500}
                            lineHeight="145%"
                            color={theme.palette.custom.blueGray900}
                          >
                            {user.name}
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={500}
                            color={theme.palette.custom.blueGray600}
                          >
                            {user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={[styles.tableCellBody, { width: "10rem" }]}>
                      <Box sx={styles.tableCellBodyContainer}>
                        <HeartSvg height={20} width={20} />
                        <Typography>{user.totalLikes.toLocaleString()}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={[styles.tableCellBody, { width: "10rem" }]}>
                      <Box sx={styles.tableCellBodyContainer}>
                        <Typography>{user.likesPerPost.toLocaleString()}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow sx={{ display: "flex", justifyContent: "center" }}>
                  <TableCell
                    colSpan={4}
                    sx={{ display: "flex", justifyContent: "center", py: 2, borderBottom: "none" }}
                  >
                    {loadingPage ? (
                      <CustomLoader size={32} loading />
                    ) : !hasMore && users.length >= 10 && tabValue !== 1 && tabValue !== 2 ? (
                      <Typography color={theme.palette.custom.blueGray600}>
                        No more users to display
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Box>
        </Table>
      </Box>
    </TableContainer>
  );
};

export default TableLeaderboard;
